import { useEffect, useState } from 'react';
import {
  fetchBillingEnabled,
  fetchSubscription,
  isSubscriptionActive,
  openBillingPortal,
  startCheckout,
  type Subscription,
} from '@/services/subscription';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const statusBadge: Record<Subscription['status'], string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  trialing: 'bg-sky-50 text-sky-700 border-sky-200',
  past_due: 'bg-amber-50 text-amber-700 border-amber-200',
  unpaid: 'bg-amber-50 text-amber-700 border-amber-200',
  canceled: 'bg-background-200 text-foreground-600 border-background-300/60',
  incomplete: 'bg-background-200 text-foreground-600 border-background-300/60',
};

export default function SubscriptionCard() {
  const { t } = useTranslation();
  const [billingEnabled, setBillingEnabled] = useState<boolean | null>(null);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      const enabled = await fetchBillingEnabled();
      if (!mounted) return;
      setBillingEnabled(enabled);
      if (enabled) {
        const s = await fetchSubscription();
        if (mounted) setSub(s);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Billing globally off, or still loading the flag → render nothing.
  if (billingEnabled !== true) return null;

  const active = isSubscriptionActive(sub);

  const handleAction = async () => {
    setBusy(true);
    setError('');
    try {
      if (active) {
        await openBillingPortal();
      } else {
        await startCheckout();
      }
    } catch (err) {
      setError((err as Error).message || 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
          <i className="ri-bank-card-line text-primary-500" />
        </span>
        <h3 className="text-sm font-heading font-semibold text-foreground-950">
          {t('subscription.title')}
        </h3>
      </div>

      {active && sub ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-foreground-600">
              {t('subscription.status')}
            </span>
            <span
              className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusBadge[sub.status]}`}
            >
              {t(`subscription.status.${sub.status}`)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-foreground-600">
              {t('subscription.plan')}
            </span>
            <span className="text-sm text-foreground-900">{t('pricing.planName')}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-foreground-600">
              {sub.cancelAtPeriodEnd
                ? t('subscription.cancelsOn')
                : sub.status === 'trialing'
                  ? t('subscription.trialEnds')
                  : t('subscription.renews')}
            </span>
            <span className="text-sm text-foreground-900">
              {formatDate(sub.status === 'trialing' ? sub.trialEnd : sub.currentPeriodEnd)}
            </span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-foreground-500 mb-4">{t('subscription.none')}</p>
      )}

      <div className="flex items-center justify-between pt-4 mt-1 border-t border-background-200/60">
        {error ? (
          <span className="text-xs text-red-500 flex items-start gap-1">
            <i className="ri-error-warning-line mt-px flex-shrink-0" />
            {error}
          </span>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleAction}
          disabled={busy}
          className="btn-primary text-xs px-4 py-2 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {active ? t('subscription.manageBilling') : t('subscription.subscribe')}
        </button>
      </div>
    </div>
  );
}
